import { InfoTooltip } from '../../components/InfoTooltip'
import { DASHBOARD_INFO, METRIC_INFO, explainReason } from './metricInfo'
import { toIndex100, type CycleView } from './signalsModel'

/**
 * Detalle por ciclo de una sesión. Vivía adentro de `KnowPhysiology`; se
 * extrajo el 2026-09-07 cuando la lista única de sesiones (`SessionsSection`)
 * pasó a mostrarlo dentro de la fila expandida.
 */
export function CyclesTable({ cycles }: { cycles: CycleView[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <ColHeader label="Ciclo" info={DASHBOARD_INFO.col_cycle} />
            <ColHeader label="Inicio" info={DASHBOARD_INFO.col_start} />
            <ColHeader label="Cardíaco" info={METRIC_INFO.r_cardio_hist} />
            <ColHeader label="Cardíaco + piel" info={METRIC_INFO.r_cardio_eda_hist} />
            <ColHeader label="HR pico" info={DASHBOARD_INFO.col_hr_peak} />
            <ColHeader label="HR fin recup." info={DASHBOARD_INFO.col_hr_recovery} />
            <ColHeader label="Recuperación" info={DASHBOARD_INFO.col_recovery} />
          </tr>
        </thead>
        <tbody>
          {cycles.map((c) => {
            const reason = c.cycle.quality.reason
            return (
              <tr key={c.cycle.trial_id}>
                <td className="text-text-primary">C{c.index}</td>
                <td>{c.offsetLabel}</td>
                <td>
                  <IndexCell value={c.cycle.r_cardio} reason={reason} />
                </td>
                <td>
                  <IndexCell value={c.cycle.r_cardio_eda} reason={reason} />
                </td>
                <td>{c.hrPeak !== null ? `${Math.round(c.hrPeak)} bpm` : '—'}</td>
                <td>
                  {c.hrRecoveryEnd !== null ? `${Math.round(c.hrRecoveryEnd)} bpm` : '—'}
                </td>
                <td>{c.recoverySeconds} s</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** `<th>` con su tooltip. Toda columna del detalle por ciclo lleva uno: son
 * todas métricas derivadas, ninguna se explica sola por el encabezado. */
function ColHeader({ label, info }: { label: string; info: React.ReactNode }) {
  return (
    <th>
      <InfoTooltipLabel label={label} info={info} />
    </th>
  )
}

/** El contenido del `<th>` sin el `<th>`: lo usa la lista de sesiones, que
 * necesita poner sus propias clases (ocultar la columna en mobile) sobre la
 * celda. */
export function InfoTooltipLabel({
  label,
  info,
}: {
  label: string
  info: React.ReactNode
}) {
  return (
    <span className="inline-flex items-center gap-xs">
      {label}
      <InfoTooltip label={label}>{info}</InfoTooltip>
    </span>
  )
}

function IndexCell({
  value,
  reason,
}: {
  value: number | null
  reason: string | null | undefined
}) {
  if (value === null) {
    // En la tabla va el badge corto: el motivo completo (una frase larga)
    // vive en el tooltip, si no una columna de 9 ciclos se vuelve ilegible.
    return (
      <span className="text-xs italic text-text-muted" title={explainReason(reason)}>
        Sin dato
      </span>
    )
  }
  return (
    <span className="text-text-primary" title={`r = ${value.toFixed(3)}`}>
      {toIndex100(value)}
    </span>
  )
}
